import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiRepeat,
  FiInfo,
} from "react-icons/fi";
import Loader from "../Loader";

/**
 * Provider API call log, with the context an admin actually needs when a user
 * reports "my light switched at the wrong time".
 *
 * Schedule-driven rows — Smart Restore (`source: "smartRestore"`) and scheduled
 * runs (`source: "cron"`) — written by the current backend carry their own
 * context: which attempt this was, the schedule and run, the device and room,
 * the commanded state, and what happens next. Every other row, and every row
 * written before that change, carries none of it.
 *
 * ⚠️ The backend metadata is the ONLY source of retry information here. Nothing
 * in this file derives an attempt, a retry, a schedule or a device from
 * timestamps or from neighbouring rows: a legacy row shows "—", never a guess.
 * (Two restores two minutes apart look exactly like a retry and are not one.)
 */

/**
 * One `apiusages` row as returned by POST /api/usage/user/logs.
 * Everything below `httpStatus` is optional: absent on legacy rows and on
 * every non-Smart-Restore call.
 *
 * @typedef {Object} UsageLog
 * @property {string}  _id
 * @property {string}  userId
 * @property {string}  action            raw action, e.g. "state change"
 * @property {string}  api               provider, e.g. "tapo"
 * @property {string}  source            "cron" | "user" | "system" | "smartRestore" | …
 * @property {number}  timestamp         ms epoch
 * @property {string}  [status]          "success" | "failed"
 * @property {number|null} [errorCode]
 * @property {number|null} [httpStatus]
 * @property {string|null} [errorMessage]
 *
 * @property {boolean} [isRetry]
 * @property {number}  [retryAttempt]    Smart Restore: 0 = Initial, N = Retry N
 * @property {number}  [httpRetryAttempt]
 * @property {string}  [attemptType]     "initial" | "retry"
 * @property {string}  [attemptLabel]    "Initial" | "Retry 1" | …
 * @property {string}  [attemptRole]     "power" | "brightness"
 * @property {number}  [maxRetries]
 * @property {string}  [retrySequenceId] shared by every attempt of one restore
 * @property {number}  [sequenceStartedAt] ms epoch of the Initial attempt
 * @property {number}  [attemptOffsetMs] planned offset of this attempt from Initial
 * @property {boolean} [retryScheduled]
 * @property {number|null} [nextRetryAttempt]
 * @property {number|null} [nextRetryAt] ms epoch, as planned by the backend
 * @property {string}  [retryReason]     "Retry 2 scheduled", "Maximum retries reached", …
 * @property {string|null} [reason]      provider's reason for a failed attempt
 * @property {string|null} [scheduleId]
 * @property {string|null} [scheduleName]
 * @property {string|null} [scheduleType] "weekly" | "once" | "monthly" | "yearly"
 * @property {string|null} [scheduleExecutionId] one run: "<scheduleId>:<occurrence ISO>"
 * @property {string|null} [deviceId]    vendor device id
 * @property {string|null} [deviceName]
 * @property {string|null} [deviceBrand] "tapo" | "honeywell"
 * @property {string|null} [deviceType]  Tapo model (e.g. "ES20M") / Honeywell device type
 * @property {string|null} [roomId]
 * @property {string|null} [roomName]
 * @property {string|null} [commandedState] "on" | "off"
 * @property {number|null} [brightness]
 * @property {string|null} [actionLabel] "ON", "OFF — 0%", "ON — 75%", "Heat — 68°", "Fan — Auto"
 */

/** What each `source` value actually means — the raw word is not obvious. */
const TRIGGERS = {
  cron: ["Scheduled run", "bg-amber-100 text-amber-800", "Fired by the dispatcher, not a person"],
  user: ["User action", "bg-blue-100 text-blue-800", "Someone acted in the app or dashboard"],
  mobile_app: ["Mobile app", "bg-indigo-100 text-indigo-800", "Request from the phone app"],
  system: ["System", "bg-slate-100 text-slate-600", "Background maintenance (token renewal, sync)"],
  smartRestore: ["Smart Restore", "bg-violet-100 text-violet-800", "Sent by Smart Restore to put a device back where its schedule left it"],
  admin: ["Admin action", "bg-rose-100 text-rose-800", "An admin acted on this user's account (e.g. Force Disconnect)"],
};

function TriggerBadge({ source }) {
  // Own keys only: `TRIGGERS["constructor"]` would otherwise resolve to a
  // prototype member and the destructuring below would throw.
  const known = typeof source === "string" && Object.prototype.hasOwnProperty.call(TRIGGERS, source);
  const [label, classes, title] = known
    ? TRIGGERS[source]
    : [typeof source === "string" && source ? source : "Unknown", "bg-slate-100 text-slate-500", "Unrecognised source value"];
  return (
    <span
      title={title}
      className={`inline-block whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${classes}`}
    >
      {label}
    </span>
  );
}

function StatusBadge({ status }) {
  const ok = status === "success";
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${
        ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
      }`}
    >
      {typeof status === "string" && status ? status : "unknown"}
    </span>
  );
}

// A value for display. Objects are serialised: rendered raw, a malformed
// non-primitive (say an object where a number belongs) makes React throw and
// blanks the whole table.
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return v;
};

/**
 * The provider's own reason, out of whatever `errorMessage` holds.
 *
 * When TP-Link rejects a device command at the HTTP level (e.g. 424) the
 * backend stores the WHOLE response body as a JSON string —
 * {"method":"setDeviceOnOff","requestId":"…","errMessage":"Device request
 * timeout","errCode":-13005} — so the useful part is buried. Pull
 * `errMessage` (or `msg` / `message`) out of it; anything that is not JSON
 * is already a plain reason and is returned as-is. The raw text stays visible
 * in Show more.
 */
function errorReason(errorMessage) {
  if (!errorMessage) return null;
  const text = String(errorMessage).trim();
  if (!text.startsWith("{")) return text;
  try {
    const body = JSON.parse(text);
    // Only a string is a reason — anything else would render as [object Object]
    // or crash React, so fall back to the raw text.
    const reason = [body?.errMessage, body?.msg, body?.message].find(
      (v) => typeof v === "string" && v.trim(),
    );
    return reason || text;
  } catch {
    return text;
  }
}

const present = (v) => v !== null && v !== undefined && v !== "";

// The new text fields are rendered straight into JSX. A value that is not a
// string (an object from a malformed row) would make React throw "Objects are
// not valid as a React child" and blank the whole table — so each one is kept
// only when it really is a non-empty string, once, before anything reads it.
const TEXT_FIELDS = [
  "attemptLabel", "attemptRole", "attemptType", "actionLabel", "commandedState",
  "scheduleId", "scheduleName", "scheduleType", "scheduleExecutionId",
  "deviceId", "deviceName", "deviceBrand", "deviceType", "roomId", "roomName",
  "reason", "retryReason", "retrySequenceId",
];
// Fields every row has had from the start. They are rendered directly too, and
// the queue writes them through a `strict: false` model that casts nothing —
// so a malformed value is possible there. Numbers become text; anything else
// non-string is dropped.
const CORE_TEXT_FIELDS = ["status", "source", "action", "api", "userId"];

const toJsonText = (v) => {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

function sanitizeLog(log) {
  if (!log || typeof log !== "object") return log;
  const out = { ...log };
  for (const k of TEXT_FIELDS) {
    if (k in out && !(typeof out[k] === "string" && out[k].trim())) out[k] = null;
  }
  for (const k of CORE_TEXT_FIELDS) {
    if (out[k] != null && typeof out[k] !== "string") {
      out[k] = typeof out[k] === "number" ? String(out[k]) : null;
    }
  }
  // errorMessage is kept, not dropped — it is the provider's own detail — but
  // as text, so the "Raw provider response" line can never receive an object.
  if (out.errorMessage != null && typeof out.errorMessage !== "string") {
    out.errorMessage = toJsonText(out.errorMessage);
  }
  // _id is rendered and used as the React key.
  if (out._id != null && typeof out._id !== "string") {
    out._id = typeof out._id === "number" ? String(out._id) : toJsonText(out._id);
  }
  return out;
}

/** Same local date + time the Timestamp column uses. */
const localDateTime = (ms) => {
  const d = new Date(Number(ms));
  return Number.isFinite(d.getTime()) ? `${d.toLocaleDateString()} ${d.toLocaleTimeString()}` : null;
};

/**
 * True only for a row that CARRIES attempt metadata. A legacy Smart Restore
 * row has none, and must render as "—" rather than as "Initial".
 */
const hasAttemptInfo = (log) => typeof log?.attemptLabel === "string" && log.attemptLabel !== "";

/** "Retry 1", or "Retry 1 · Brightness" for the best-effort follow-up call. */
function attemptText(log) {
  if (!hasAttemptInfo(log)) return null;
  return log.attemptRole === "brightness" ? `${log.attemptLabel} · Brightness` : log.attemptLabel;
}

/** Device as the backend named it — the vendor id only when there is no name. */
const deviceText = (log) => log?.deviceName || log?.deviceId || null;

const capitalise = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : v);

/** "weekly" → "Weekly". An unknown value is shown as given, never replaced. */
const scheduleTypeText = (log) => (log?.scheduleType ? capitalise(log.scheduleType) : null);

/** "tapo" → "Tapo", "honeywell" → "Honeywell". */
const brandText = (log) => (log?.deviceBrand ? capitalise(log.deviceBrand) : null);

/**
 * "Tapo · ES20M" — brand and type, each once, and neither when it merely
 * repeats something already shown (the name, or each other).
 */
function deviceMetaText(log) {
  const seen = new Set([String(deviceText(log) || "").toLowerCase()]);
  const parts = [];
  for (const part of [brandText(log), log?.deviceType]) {
    const key = String(part || "").toLowerCase();
    if (part && !seen.has(key)) {
      seen.add(key);
      parts.push(part);
    }
  }
  return parts.length ? parts.join(" · ") : null;
}

/**
 * The device type, unless it only repeats the device name or the brand —
 * the same rule, case-insensitive, as the table's "Tapo · ES20M" line.
 */
function distinctDeviceType(log) {
  const type = log?.deviceType;
  if (!type) return null;
  const key = type.toLowerCase();
  const others = [deviceText(log), log?.deviceBrand].map((v) => String(v || "").toLowerCase());
  return others.includes(key) ? null : type;
}

/** The room by name — its id only when there is no name. */
const roomText = (log) => log?.roomName || log?.roomId || null;

/**
 * A long id, shortened for display: "6ab36f47…:2026-09-25T22:00:00.000Z" for
 * a run id (the schedule id is shortened, the occurrence kept — it is the part
 * that tells two runs apart), "abcdef12…9f3e" otherwise.
 */
function shortId(id) {
  if (!id) return null;
  const at = id.indexOf(":");
  if (at > 0) {
    const head = id.slice(0, at);
    return head.length > 8 ? `${head.slice(0, 8)}…${id.slice(at)}` : id;
  }
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

/**
 * True when the row carries any schedule-driven context at all. A cron row
 * without it (every one written before the backend change) renders exactly as
 * before — no empty section of dashes.
 */
const hasScheduleContext = (log) =>
  hasAttemptInfo(log) ||
  Boolean(log?.scheduleName || log?.scheduleExecutionId || log?.deviceName || log?.roomName);

/** A shortened id with its full value in a tooltip and one-click copy. */
function IdValue({ id, label }) {
  const [copied, setCopied] = useState(false);
  // "Copied" is a confirmation, not a state: it reverts, and the timer goes
  // with the component.
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  if (!id) return "—";
  const copy = (e) => {
    e.stopPropagation();
    try {
      const done = navigator?.clipboard?.writeText?.(id);
      if (done && typeof done.then === "function") {
        done.then(() => setCopied(true), () => {});
      }
    } catch {
      // Clipboard unavailable (insecure context, old browser): the full value
      // is still in the tooltip.
    }
  };
  return (
    <span className="inline-flex max-w-full items-center gap-1.5">
      <span title={id} className="truncate font-mono text-[11px]">
        {shortId(id)}
      </span>
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy ${label}`}
        title={`Copy full ${label}`}
        className="shrink-0 rounded px-1 text-[10px] font-semibold text-indigo-600 hover:bg-indigo-50"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}

/**
 * The failure reason: the backend's `reason` when it sent one, otherwise the
 * reason inside `errorMessage` (legacy rows, non-Smart-Restore calls).
 */
const failureReason = (log) => log?.reason || errorReason(log?.errorMessage);

/**
 * Did this attempt fail? `status` alone is not enough: a 200 with no result
 * code is stored `status: "success"` yet the backend treats it as a FAILED
 * attempt, and says so with a `reason` (set only when an attempt failed).
 * One test, used by the table cell, the panel and the sequence chips alike.
 */
const attemptFailed = (log) => log?.status !== "success" || Boolean(log?.reason);

/**
 * What happens after this attempt, exactly as the backend decided it. The
 * next-retry time is the backend's `nextRetryAt`, formatted — never computed.
 * @returns {{ text: string, at: string|null, scheduled: boolean, final: boolean } | null}
 */
function nextStep(log) {
  if (!hasAttemptInfo(log)) return null;
  // A success normally ends the sequence. The exception is a 200 with no
  // result code, which the backend retries and marks retryScheduled — so the
  // flag decides, not the status.
  if (!attemptFailed(log) && !log.retryScheduled) return null;
  if (log.retryScheduled) {
    const text =
      log.retryReason || (present(log.nextRetryAttempt) ? `Retry ${log.nextRetryAttempt} scheduled` : "Retry scheduled");
    return {
      text,
      at: present(log.nextRetryAt) ? localDateTime(log.nextRetryAt) : null,
      scheduled: true,
      final: false,
    };
  }
  // No retry follows. `final` means THIS was the last try of the sequence; a
  // best-effort brightness call is never retried on its own, which is not the
  // same thing and is not shown as a failure of the sequence.
  return log.retryReason
    ? { text: log.retryReason, at: null, scheduled: false, final: log.attemptRole !== "brightness" }
    : null;
}

/**
 * The retry line's wording, shared by the table cell and the panel so the two
 * can never say it differently. No prefix when the backend text already says
 * there is no retry ("Not retried — …", "… not retried on its own").
 */
function retryWording(next) {
  if (!next) return null;
  if (next.scheduled) return { prefix: "Next: ", text: next.text };
  if (/not retried/i.test(next.text)) return { prefix: "", text: next.text };
  return { prefix: "No further retry: ", text: next.text };
}

/** "T+3 min" for a backend-planned offset. */
const offsetText = (ms) => (present(ms) && Number.isFinite(Number(ms)) ? `T+${Number(ms) / 60000} min` : null);

/** A small inline chip for the attempt. */
function AttemptChip({ log }) {
  const label = attemptText(log);
  if (!label) return null;
  const retry = log.isRetry === true || log.attemptType === "retry";
  return (
    <span
      title={offsetText(log.attemptOffsetMs) ? `Planned at ${offsetText(log.attemptOffsetMs)} from the initial attempt` : undefined}
      className={`mt-1 inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
        retry ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"
      }`}
    >
      {retry && <FiRepeat size={10} />}
      {label}
    </span>
  );
}

function relativeTime(ts) {
  const ms = Date.now() - Number(ts);
  if (!Number.isFinite(ms)) return "";
  const min = Math.round(ms / 60000);
  if (Math.abs(min) < 1) return "just now";
  if (Math.abs(min) < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

/** One "label: value" line in the expanded panel — a labelled group, so the
 *  value can be found by its label (screen readers, and the tests). */
function Detail({ label, children, muted }) {
  return (
    <div role="group" aria-label={label} className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 break-words text-xs ${muted ? "text-slate-400 italic" : "text-slate-800"}`}>
        {children}
      </p>
    </div>
  );
}

/** Section wording per source — cron rows never get Smart Restore terms. */
const SECTIONS = {
  smartRestore: {
    title: "Smart Restore",
    region: "Smart Restore details",
    ring: "ring-violet-200",
    activeRing: "ring-violet-400",
    tone: "text-violet-700",
    siblings: "Attempts in this sequence on this page",
    noMetadata:
      "This Smart Restore entry carries no attempt metadata — typically because it was logged before attempt tracking existed — so its attempt, schedule and device are unknown. Nothing here is inferred from timestamps.",
  },
  cron: {
    title: "Scheduled Run",
    region: "Scheduled run details",
    ring: "ring-amber-200",
    activeRing: "ring-amber-400",
    tone: "text-amber-700",
    siblings: "Attempts of this action in this run on this page",
    noMetadata: null, // a cron row without context gets no section at all
  },
};

/** The retry line exactly as the backend decided it. */
function RetryLine({ next, log }) {
  if (!next) return log.retryReason || "—";
  const w = retryWording(next);
  const text = next.scheduled ? w.text : `${w.prefix}${w.text}`;
  return (
    <span
      className={`font-semibold ${
        next.scheduled ? "text-amber-700" : next.final ? "text-rose-700" : "text-slate-600"
      }`}
    >
      {text}
    </span>
  );
}

/**
 * The schedule-driven block of Show more — "Smart Restore" or "Scheduled
 * Run". Every field comes straight from the row; a missing one is "—" (a
 * legacy Smart Restore row) or omitted where it cannot apply.
 */
function ScheduleContextDetails({ log, logs, next, device }) {
  const kind = SECTIONS[log.source] || SECTIONS.cron;
  const withAttempt = hasAttemptInfo(log);
  const failed = attemptFailed(log);
  const reasonText = failed ? failureReason(log) : null;
  const room = roomText(log);

  // Other attempts of the SAME sequence, by the backend's sequence id — never
  // by timestamp proximity. Only rows on the current page can be shown, and
  // the panel says so.
  const siblings = withAttempt && log.retrySequenceId
    ? logs
        .filter((l) => l.retrySequenceId === log.retrySequenceId)
        .sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
    : [];

  return (
    <section
      aria-label={kind.region}
      className={`mt-5 rounded-lg bg-white px-4 py-3 ring-1 ${kind.ring}`}
    >
      <p className={`mb-3 text-[10px] font-black uppercase tracking-widest ${kind.tone}`}>
        {kind.title}
      </p>

      <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
        {/* What and when */}
        <Detail label="Attempt" muted={!withAttempt}>{attemptText(log) || "—"}</Detail>
        <Detail label="Action" muted={!log.actionLabel}>{log.actionLabel || "—"}</Detail>
        <Detail label="Schedule" muted={!log.scheduleName}>{log.scheduleName || "—"}</Detail>
        {scheduleTypeText(log) && <Detail label="Schedule type">{scheduleTypeText(log)}</Detail>}
        {log.scheduleExecutionId && (
          <div className="col-span-2">
            <Detail label="Run ID">
              <IdValue id={log.scheduleExecutionId} label="run ID" />
            </Detail>
          </div>
        )}

        {/* Where */}
        <Detail label="Device" muted={!device}>
          <span className={log.deviceName ? "" : "break-all font-mono text-[11px]"}>{device || "—"}</span>
          {/* The vendor id as a second line only when a NAME is shown —
              never the same id twice. */}
          {log.deviceName && log.deviceId && (
            <span className="block break-all font-mono text-[10px] text-slate-400">{log.deviceId}</span>
          )}
        </Detail>
        {brandText(log) && <Detail label="Brand">{brandText(log)}</Detail>}
        {distinctDeviceType(log) && <Detail label="Device type">{distinctDeviceType(log)}</Detail>}
        <Detail label="Room" muted={!room}>
          {room ? (
            <span className={log.roomName ? "" : "break-all font-mono text-[11px]"}>{room}</span>
          ) : (
            "—"
          )}
        </Detail>

        {/* Outcome */}
        <Detail label="Status">{fmt(log.status)}</Detail>
        <Detail label="Reason" muted={!reasonText}>{reasonText || "—"}</Detail>
        {/* fmt(): numbers today, but a malformed non-number must render as
            text rather than crash React. */}
        {failed && log.errorCode != null && <Detail label="Error code">{fmt(log.errorCode)}</Detail>}
        {/* Shown for successes too: the general panel no longer repeats it. */}
        {present(log.httpStatus) && <Detail label="HTTP status">{fmt(log.httpStatus)}</Detail>}
        {/* The provider's raw answer, in full — only when it says more than the
            Reason line already does (a plain message IS the reason). */}
        {failed && log.errorMessage && log.errorMessage !== reasonText && (
          <div className="col-span-2 md:col-span-4">
            <Detail label="Error details">
              <span className="break-all font-mono text-[11px]">{log.errorMessage}</span>
            </Detail>
          </div>
        )}
        <div className="col-span-2">
          <Detail label="Retry" muted={!withAttempt}>
            {withAttempt ? <RetryLine next={next} log={log} /> : "—"}
          </Detail>
        </div>

        {/* Sequence */}
        {next?.at && <Detail label="Next attempt">{next.at}</Detail>}
        {withAttempt && present(log.retryAttempt) && present(log.maxRetries) && (
          <Detail label="Attempt / max retries">{`${log.retryAttempt} / ${log.maxRetries}`}</Detail>
        )}
        {offsetText(log.attemptOffsetMs) && (
          <Detail label="Planned at">{offsetText(log.attemptOffsetMs)} from initial</Detail>
        )}
        {present(log.sequenceStartedAt) && localDateTime(log.sequenceStartedAt) && (
          <Detail label="Sequence started">{localDateTime(log.sequenceStartedAt)}</Detail>
        )}
        {log.retrySequenceId && (
          <div className="col-span-2">
            <Detail label="Retry ID">
              <IdValue id={log.retrySequenceId} label="retry ID" />
            </Detail>
          </div>
        )}
      </div>

      {siblings.length > 1 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            {kind.siblings}
          </p>
          <ol className="mt-1.5 flex flex-wrap gap-1.5">
            {siblings.map((s) => (
              <li
                key={s._id}
                className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ring-1 ${
                  s._id === log._id ? kind.activeRing : "ring-slate-200"
                } ${attemptFailed(s) ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}
              >
                {attemptText(s) || "—"} — {attemptFailed(s) ? "Failed" : "Success"}
              </li>
            ))}
          </ol>
        </div>
      )}

      {!withAttempt && kind.noMetadata && (
        <p className="mt-3 text-[11px] italic text-slate-400">{kind.noMetadata}</p>
      )}
    </section>
  );
}

/**
 * @param {{ logs?: UsageLog[], loading?: boolean, user?: { userName?: string } }} props
 */
export default function UsageLogsTable({ logs: rawLogs = [], loading, user }) {
  const [expandedId, setExpandedId] = useState(null);
  // A null or non-object entry would crash the row renderer on `log._id`.
  const logs = Array.isArray(rawLogs)
    ? rawLogs.filter((l) => l && typeof l === "object").map(sanitizeLog)
    : [];
  const navigate = useNavigate();

  // Raw ids are not what an admin recognises. Show the name and make it the
  // way into that user's drill-down; the id stays only as the navigation
  // target. When the name is unknown the row is still clickable, because the
  // id is what the route needs either way.
  const UserLink = ({ userId, className = "" }) => {
    if (!userId) return <span className="text-slate-400">—</span>;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/admin/dash/users/${userId}`);
        }}
        title="Open this user's detail page"
        className={`text-left font-semibold text-slate-700 hover:text-indigo-600 hover:underline ${className}`}
      >
        {user?.userName || "Unknown user"}
      </button>
    );
  };

  const COLS = 7;

  return (
    <div className="overflow-x-auto min-h-[400px]">
      <table className="w-full text-left">
        <thead className="bg-slate-50/80">
          <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            <th className="px-5 py-4">Timestamp</th>
            <th className="px-5 py-4">User</th>
            <th className="px-5 py-4">Trigger</th>
            <th className="px-5 py-4">Event</th>
            <th className="px-5 py-4 text-center">Status</th>
            <th className="px-5 py-4">Error</th>
            <th className="px-5 py-4 text-right"> </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 text-sm">
          {loading ? (
            <tr>
              <td colSpan={COLS} className="p-20 text-center">
                <Loader />
              </td>
            </tr>
          ) : logs.length === 0 ? (
            <tr>
              <td colSpan={COLS} className="p-16 text-center text-sm text-slate-400">
                No API calls recorded for this user and filter.
              </td>
            </tr>
          ) : (
            logs.map((log) => {
              const open = expandedId === log._id;
              const hasError =
                log.errorCode != null || log.errorMessage || log.httpStatus >= 400;
              const isSmartRestore = log.source === "smartRestore";
              const withAttempt = hasAttemptInfo(log);
              const next = nextStep(log);
              const device = deviceText(log);
              const deviceMeta = deviceMetaText(log);
              const room = roomText(log);
              const scheduleType = scheduleTypeText(log);
              // Smart Restore rows always get their section (a legacy one says
              // what is missing); a cron row only when it carries context, so
              // every older cron row renders exactly as before.
              const showContext =
                isSmartRestore || (log.source === "cron" && hasScheduleContext(log));
              const failed = attemptFailed(log);
              const ts = new Date(Number(log.timestamp));
              const tsOk = Number.isFinite(ts.getTime());
              // "code -13005" / "HTTP 424" — only when there is one. A network
              // error carries neither, and used to read "HTTP null".
              const errorHead =
                log.errorCode != null
                  ? `code ${fmt(log.errorCode)}`
                  : present(log.httpStatus)
                    ? `HTTP ${fmt(log.httpStatus)}`
                    : null;

              return (
                <Fragment key={log._id}>
                  <tr className="transition-all hover:bg-blue-50/30">
                    <td className="px-5 py-4 align-top">
                      {/* "—" rather than "Invalid Date", as the panel does. */}
                      <div className="text-[11px] font-bold text-slate-700">
                        {tsOk ? ts.toLocaleDateString() : "—"}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {tsOk ? ts.toLocaleTimeString() : ""}
                      </div>
                      <div className="text-[10px] text-slate-300">
                        {relativeTime(log.timestamp)}
                      </div>
                    </td>

                    <td className="px-5 py-4 align-top">
                      <UserLink userId={log.userId} className="text-xs" />
                    </td>

                    <td className="px-5 py-4 align-top">
                      <TriggerBadge source={log.source} />
                    </td>

                    <td className="px-5 py-4 align-top">
                      {/* The commanded state when the backend recorded one
                          ("ON — 75%"); otherwise the raw action, as before. */}
                      <div className="font-bold text-slate-800">
                        {log.actionLabel || fmt(log.action)}
                      </div>
                      {log.actionLabel && log.action && (
                        <div className="text-[10px] text-slate-400">{log.action}</div>
                      )}
                      <div className="text-[10px] font-black uppercase tracking-widest text-blue-500">
                        {fmt(log.api)}
                      </div>
                      {(log.scheduleName || device || room) && (
                        <div className="mt-1.5 max-w-[240px] space-y-0.5 text-[11px] text-slate-600">
                          {log.scheduleName && (
                            <p
                              className="flex min-w-0 items-center gap-1"
                              title={scheduleType ? `${log.scheduleName} · ${scheduleType}` : log.scheduleName}
                            >
                              <span className="shrink-0 text-slate-400">Schedule</span>
                              {/* The name gives way to a long title; the type
                                  tag never disappears into the ellipsis. */}
                              <span className="truncate">{log.scheduleName}</span>
                              {scheduleType && (
                                <span className="shrink-0 rounded bg-slate-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                                  {scheduleType}
                                </span>
                              )}
                            </p>
                          )}
                          {device && (
                            <div title={deviceMeta ? `${device} · ${deviceMeta}` : device}>
                              <p className="truncate">
                                <span className="text-slate-400">Device </span>
                                <span className={log.deviceName ? "" : "font-mono text-[10px]"}>{device}</span>
                              </p>
                              {deviceMeta && (
                                <p className="truncate pl-[42px] text-[10px] text-slate-400">{deviceMeta}</p>
                              )}
                            </div>
                          )}
                          {room && (
                            <p className="truncate" title={room}>
                              <span className="text-slate-400">Room </span>
                              <span className={log.roomName ? "" : "font-mono text-[10px]"}>{room}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </td>

                    <td className="px-5 py-4 text-center align-top">
                      <StatusBadge status={log.status} />
                      {withAttempt && (
                        <div>
                          <AttemptChip log={log} />
                        </div>
                      )}
                      {/* The legacy retry marker — only for rows WITHOUT backend
                          attempt metadata, so a new row never shows two. */}
                      {!withAttempt && log.isRetry === true && (
                        <div
                          title={`Retry attempt ${log.retryAttempt}`}
                          className="mt-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-amber-600"
                        >
                          <FiRepeat size={10} /> retry {fmt(log.retryAttempt)}
                        </div>
                      )}
                    </td>

                    <td className="px-5 py-4 align-top">
                      {hasError || next || (failed && failureReason(log)) ? (
                        <div className="max-w-[240px]">
                          {hasError && errorHead && (
                            <div className="flex items-center gap-1 text-[11px] font-bold text-rose-700">
                              <FiAlertCircle size={11} />
                              {errorHead}
                            </div>
                          )}
                          {(log.reason || log.errorMessage) && (
                            <p
                              title={log.errorMessage || log.reason}
                              className="mt-0.5 line-clamp-2 text-[10px] text-slate-500"
                            >
                              {isSmartRestore && <span className="text-slate-400">Reason: </span>}
                              {failureReason(log)}
                            </p>
                          )}
                          {next && (
                            <p
                              className={`mt-1 text-[10px] font-semibold ${
                                next.scheduled ? "text-amber-700" : next.final ? "text-rose-700" : "text-slate-500"
                              }`}
                            >
                              {retryWording(next).prefix && (
                                <span className="font-normal text-slate-400">
                                  {retryWording(next).prefix}
                                </span>
                              )}
                              {next.text}
                              {next.at && <span className="font-normal text-slate-500"> · {next.at}</span>}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>

                    <td className="px-5 py-4 text-right align-top">
                      <button
                        type="button"
                        onClick={() => setExpandedId(open ? null : log._id)}
                        aria-expanded={open}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        {open ? "Less" : "Show more"}
                        {open ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                      </button>
                    </td>
                  </tr>

                  {open && (
                    <tr className="bg-slate-50/70">
                      <td colSpan={COLS} className="px-6 py-5">
                        <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
                          <Detail label="Log id">
                            <span className="font-mono">{log._id}</span>
                          </Detail>
                          <Detail label="User">
                            <UserLink userId={log.userId} className="text-xs" />
                          </Detail>
                          <Detail label="Exact time">
                            {/* toISOString() THROWS on an invalid date, which
                                would blank the table on expand. */}
                            {Number.isFinite(new Date(Number(log.timestamp)).getTime())
                              ? new Date(Number(log.timestamp)).toISOString()
                              : "—"}
                          </Detail>
                          <Detail label="Provider">{fmt(log.api)}</Detail>

                          <Detail label="Action">{fmt(log.action)}</Detail>
                          <Detail label="Trigger source">{fmt(log.source)}</Detail>
                          {/* A schedule-driven row states its attempt in its
                              own section below; "Retry: No" here would contradict
                              a "Retry 2" there, and on a legacy Smart Restore row
                              it would be a claim nobody recorded. */}
                          {!showContext && (
                            <Detail label="Retry">
                              {log.isRetry ? `Yes · attempt ${log.retryAttempt}` : "No"}
                            </Detail>
                          )}
                          {/* On a row with a schedule section, the OUTCOME (status,
                              codes, reason) is stated there — once. Every other
                              row keeps these lines exactly as before. */}
                          {!showContext && (
                            <>
                              <Detail label="Status">{fmt(log.status)}</Detail>

                              <Detail label="Provider error code">{fmt(log.errorCode)}</Detail>
                              <Detail label="HTTP status">{fmt(log.httpStatus)}</Detail>
                              <div className="col-span-2">
                                <Detail label="Error reason">{fmt(errorReason(log.errorMessage))}</Detail>
                              </div>
                            </>
                          )}
                          {/* Rows with a schedule section show this there, as
                              "Error details" — never twice. */}
                          {!showContext && log.errorMessage && errorReason(log.errorMessage) !== log.errorMessage && (
                            <div className="col-span-2 md:col-span-4">
                              <Detail label="Raw provider response">
                                <span className="break-all font-mono text-[11px]">{log.errorMessage}</span>
                              </Detail>
                            </div>
                          )}
                        </div>

                        {showContext && (
                          <ScheduleContextDetails log={log} logs={logs} next={next} device={device} />
                        )}

                        {/* Stated plainly, and ONLY for a row that really lacks
                            it — blank fields would read as "no schedule", a
                            different and wrong claim. New schedule-driven rows
                            carry schedule and device, so they never show this. */}
                        {!log.scheduleName && !device && (
                          <div className="mt-5 flex items-start gap-2 rounded-lg bg-white px-3 py-2.5 ring-1 ring-slate-200">
                            <FiInfo size={13} className="mt-0.5 shrink-0 text-slate-400" />
                            <p className="text-[11px] leading-relaxed text-slate-500">
                              <span className="font-semibold text-slate-600">
                                This entry records no schedule or device.
                              </span>{" "}
                              Only schedule-driven calls (Smart Restore and scheduled runs) logged
                              by the current backend carry them; other calls, and entries written
                              before that, do not. To
                              attribute this call, cross-reference the timestamp with that
                              user's <span className="font-semibold">Upcoming</span> tab or the{" "}
                              <span className="font-semibold">Global History Logs</span> page.
                            </p>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
